import { AiOutlined, DownloadUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Modal,
  Row,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import React, { useEffect, useMemo, useState } from 'react';

import type {
  GenerationConfigSource,
  GenerationContext,
} from './ConfigContentGeneration.helpers';
import {
  buildContentConfigGenerationCsv,
  contentGenerationFilename,
  downloadContentConfigGenerationCsv,
  parseContentConfigGenerationResults,
} from './ConfigContentGeneration.helpers';
import { contentConfigModalStyle } from './theme';

const CONFIG_SOURCE_NOTICE: Partial<Record<GenerationConfigSource, string>> = {
  draft: 'This product offering is a DRAFT. Content is generated from the unsaved configuration on screen.',
  'local-edits': 'Content is generated from your unsaved local edits, not the published configuration.',
  pending: 'This product offering is PENDING APPROVAL. Content is generated from the pending configuration.',
};

interface Props {
  audienceKeys: string[];
  audienceLabelsByKey: Record<string, string>;
  audienceWhitelistByKey: Record<string, boolean>;
  configSource: GenerationConfigSource;
  context: GenerationContext;
  errorsByAudienceKey: Record<string, string>;
  isFailure: boolean;
  isLoading: boolean;
  onClose: () => void;
  onRegenerate: (audienceKeys?: string[]) => void;
  onRegenerateAudience: (audienceKey: string) => void;
  onRetry: () => void;
  open: boolean;
  readyAudienceKeys: string[];
  responsesByAudienceKey: Record<string, any>;
}

const ContentGenerationModal = ({
  audienceKeys,
  audienceLabelsByKey,
  audienceWhitelistByKey,
  configSource,
  context,
  errorsByAudienceKey,
  isFailure,
  isLoading,
  onClose,
  onRegenerate,
  onRegenerateAudience,
  onRetry,
  open,
  readyAudienceKeys,
  responsesByAudienceKey,
}: Props) => {
  const [activeAudienceKey, setActiveAudienceKey] = useState('');
  const readyAudienceKeySet = useMemo(() => {
    const displayedAudienceKeys = new Set(audienceKeys);
    return new Set(
      readyAudienceKeys.filter((audienceKey) => displayedAudienceKeys.has(audienceKey)),
    );
  }, [audienceKeys, readyAudienceKeys]);
  const itemsByAudienceKey = useMemo(
    () => parseContentConfigGenerationResults(audienceKeys, responsesByAudienceKey),
    [audienceKeys, responsesByAudienceKey],
  );
  const totalItems = audienceKeys.reduce(
    (total, audienceKey) => total + (
      readyAudienceKeySet.has(audienceKey) ? (itemsByAudienceKey[audienceKey] || []).length : 0
    ),
    0,
  );

  useEffect(() => {
    if (!open) return;
    if (!audienceKeys.includes(activeAudienceKey)) {
      setActiveAudienceKey(audienceKeys[0] || '');
    }
  }, [activeAudienceKey, audienceKeys, open]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !open) return undefined;
    const ignoreResizeObserverDeliveryWarning = (event: ErrorEvent) => {
      if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', ignoreResizeObserverDeliveryWarning, true);
    return () => window.removeEventListener('error', ignoreResizeObserverDeliveryWarning, true);
  }, [open]);

  const download = (downloadAudienceKeys: string[], audienceKey?: string) => {
    const csv = buildContentConfigGenerationCsv(
      context,
      downloadAudienceKeys,
      itemsByAudienceKey,
    );
    downloadContentConfigGenerationCsv(
      contentGenerationFilename(context.product, audienceKey),
      csv,
    );
  };

  const tabs = audienceKeys.map((audienceKey) => {
    const items = itemsByAudienceKey[audienceKey] || [];
    const audienceLabel = audienceLabelsByKey[audienceKey] || audienceKey;
    const isWhitelisted = Boolean(audienceWhitelistByKey[audienceKey]);
    const isReady = readyAudienceKeySet.has(audienceKey);
    // Mystique rejects an in-progress config it cannot resolve (unknown CVP,
    // non-ACTIVE audience) with a reason worth showing verbatim.
    const audienceError = errorsByAudienceKey[audienceKey] || '';
    let audienceResult;
    if (!isReady) {
      audienceResult = (
        <Flex vertical align="center" justify="center" gap={8} className="cc-generation-tab-waiting">
          {isLoading && <Spin />}
          <Typography.Body level={2} type="secondary">
            {isFailure ? 'No result was returned for this audience.' : 'Waiting for this audience…'}
          </Typography.Body>
          {audienceError && (
            <Typography.Body level={2} type="danger">{audienceError}</Typography.Body>
          )}
        </Flex>
      );
    } else if (items.length === 0) {
      audienceResult = <Empty description="No content was returned for this audience." />;
    } else {
      audienceResult = (
        <Row gutter={[16, 16]}>
          {items.map((item, index) => {
            const failedReview = item.reviewResult === 'fail';
            return (
              <Col key={`${audienceKey}-${index}`} xs={24} md={12}>
                <Card bordered={false} className="cc-generation-card">
                  <Flex vertical gap="middle" className="cc-generation-card-content">
                    <div className="cc-generation-copy">
                      <Typography.Body level={2} strong>{item.title}</Typography.Body>
                      <Typography.Body level={2} type="secondary" className="cc-generation-message">
                        {item.message}
                      </Typography.Body>
                    </div>
                    <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                      {item.cvp ? <Tag>CVP: {item.cvp}</Tag> : <span />}
                      <Tooltip title={item.reviewReason}>
                        <Tag color={failedReview ? 'error' : 'success'}>Score: {item.score}</Tag>
                      </Tooltip>
                    </Flex>
                  </Flex>
                </Card>
              </Col>
            );
          })}
        </Row>
      );
    }
    const isActiveTab = activeAudienceKey === audienceKey;

    return {
      key: audienceKey,
      label: (
        <span
          className={isReady
            ? 'cc-generation-tab-label cc-generation-tab-label-ready'
            : 'cc-generation-tab-label cc-generation-tab-label-waiting'}
        >
          <span>{audienceLabel}</span>
          {isReady && (
            <span className="cc-generation-tab-count">· {items.length}</span>
          )}
        </span>
      ),
      children: isActiveTab ? (
        <Flex vertical gap="middle" className="cc-generation-panel">
          <Flex vertical gap={12}>
            <div>
              <Flex align="center" gap={8} wrap="wrap">
                <Typography.Body level={2} strong>{audienceLabel}</Typography.Body>
                <Tag color={isWhitelisted ? 'success' : 'warning'}>
                  {isWhitelisted ? 'Whitelisted' : 'Non-whitelisted'}
                </Tag>
              </Flex>
              <Typography.Metadata type="secondary" className="cc-generation-definition">
                Audience key: {audienceKey}
              </Typography.Metadata>
            </div>
            <Flex gap={8} wrap="wrap">
              <Button
                size="small"
                icon={<AiOutlined />}
                disabled={isLoading}
                onClick={() => onRegenerateAudience(audienceKey)}
              >
                Regenerate audience
              </Button>
              <Button
                size="small"
                icon={<DownloadUniversal />}
                disabled={!isReady || items.length === 0}
                onClick={() => download([audienceKey], audienceKey)}
              >
                Download audience CSV
              </Button>
            </Flex>
          </Flex>

          {audienceResult}
        </Flex>
      ) : null,
    };
  });

  let generationStatus = null;
  if (isLoading) {
    generationStatus = (
      <Flex align="center" gap={8}>
        <Spin size="small" />
        <Typography.Body level={2} type="secondary">
          {readyAudienceKeySet.size} of {audienceKeys.length} ready
        </Typography.Body>
      </Flex>
    );
  } else if (isFailure) {
    generationStatus = (
      <Flex vertical gap={8}>
        <Alert
          type="error"
          showIcon
          message="Some content could not be generated. Ready audiences are still available below."
        />
        <Flex>
          <Button type="primary" icon={<AiOutlined />} onClick={onRetry}>Try again</Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <Modal
      title={(
        <Flex align="center" gap={8}>
          <AiOutlined />
          <span>Generated Content</span>
          {!isLoading && !isFailure && <Tag>{totalItems}</Tag>}
        </Flex>
      )}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      keyboard={false}
      width={1040}
      style={contentConfigModalStyle}
      className="cc-modal cc-generation-modal"
      footer={(
        <Flex justify="space-between" align="center" gap={12} wrap="wrap">
          <Typography.Metadata type="secondary">
            {audienceKeys.length} {audienceKeys.length === 1 ? 'audience' : 'audiences'} · {totalItems} results
          </Typography.Metadata>
          <Flex gap={8}>
            <Button onClick={onClose}>Close</Button>
            <Button
              icon={<AiOutlined />}
              disabled={isLoading}
              onClick={() => onRegenerate(audienceKeys)}
            >
              Regenerate content
            </Button>
            <Button
              type="primary"
              icon={<DownloadUniversal />}
              disabled={isLoading
                || isFailure
                || readyAudienceKeySet.size !== audienceKeys.length
                || totalItems === 0}
              onClick={() => download(audienceKeys)}
            >
              Download all CSV
            </Button>
          </Flex>
        </Flex>
      )}
    >
      <Flex vertical gap="middle">
        <Typography.Body level={2} type="secondary">
          {context.product} · {context.country} · {context.language.toUpperCase()}
          {context.objectiveName || context.objectiveID
            ? ` · ${context.objectiveName || context.objectiveID}`
            : ''}
        </Typography.Body>
        {CONFIG_SOURCE_NOTICE[configSource] && (
          <Alert
            type="info"
            showIcon
            message={CONFIG_SOURCE_NOTICE[configSource]}
          />
        )}
        {generationStatus}
        <Tabs
          type="pill"
          activeKey={activeAudienceKey}
          onChange={setActiveAudienceKey}
          items={tabs}
        />
      </Flex>
    </Modal>
  );
};

export default ContentGenerationModal;
